import * as React from 'react'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import styles from './ImiProgressBanner.module.css'

type ImiProgressBannerProps = {
  messageKey?: string
  fallback?: string
  /** How many items have been processed so far (for a determinate bar). */
  current?: number
  /** Total number of items. When > 0 the bar shows real progress, otherwise
   *  it falls back to an indeterminate animation. */
  total?: number
}

export const ImiProgressBanner: React.FC<ImiProgressBannerProps> = ({
  messageKey = 'exportModule.submitting-in-progress',
  fallback = 'Trwa zgłaszanie…',
  current,
  total,
}) => {
  const { t } = useLocalize()

  const isDeterminate = typeof total === 'number' && total > 0
  const done = Math.min(Math.max(current ?? 0, 0), total ?? 0)
  const percent = isDeterminate ? Math.round((done / (total as number)) * 100) : 0

  return (
    <div className="tw-flex tw-w-[340px] tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-base-300 tw-bg-white tw-p-3.5 tw-shadow-xl">
      <div className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary/10">
        <LoaderIcon className="tw-h-5 tw-w-5 tw-animate-spin tw-text-primary" />
      </div>

      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1.5">
        <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2">
          <span className="tw-truncate tw-text-sm tw-font-semibold tw-text-content-base">
            {t(messageKey) || fallback}
          </span>
          {isDeterminate && (
            <span className="tw-shrink-0 tw-text-xs tw-font-semibold tw-tabular-nums tw-text-primary">
              {done}/{total}
            </span>
          )}
        </div>

        <div className={styles.track}>
          {isDeterminate ? (
            <div className={styles.fill} style={{ width: `${percent}%` }} />
          ) : (
            <div className={styles.indeterminate} />
          )}
        </div>
      </div>
    </div>
  )
}
